import { SvgIcon } from '@mui/material';
import type { Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import { forwardRef, useImperativeHandle } from 'react';

import type { AnimatedIconProps, AnimeatableIconRef } from './types';
import { pathVariants } from './types';

const cogVariants: Variants = {
  visible: { rotate: 0 },
  animate: {
    rotate: 180,
    transition: { type: 'spring', stiffness: 50, damping: 10 },
  },
};

const CalendarCogIcon = forwardRef<AnimeatableIconRef, AnimatedIconProps>(
  ({ size = 28, strokeWidth = 2, titleAccess, color, fontSize }, ref) => {
    const pathControls = useAnimation();
    const cogControls = useAnimation();

    useImperativeHandle(ref, () => ({
      startAnimation: async () => {
        const staggerDelay = 0.07;
        const duration = 0.12;
        await Promise.all([
          (async () => {
            await pathControls.start((i) => ({
              pathLength: 0,
              opacity: 0,
              transition: { delay: i * staggerDelay, duration },
            }));
            await pathControls.start((i) => ({
              pathLength: 1,
              opacity: 1,
              transition: { delay: i * staggerDelay, duration },
            }));
          })(),
          cogControls.start('animate'),
        ]);
      },
      stopAnimation: async () => {
        await Promise.all([
          pathControls.start('visible'),
          cogControls.start('visible'),
        ]);
      },
    }));

    return (
      <SvgIcon
        titleAccess={titleAccess}
        color={color}
        fontSize={fontSize}
        sx={fontSize ? undefined : { width: size, height: size }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path
            variants={pathVariants}
            initial="visible"
            animate={pathControls}
            custom={0}
            d="M21 10.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6"
          />
          <motion.path
            variants={pathVariants}
            initial="visible"
            animate={pathControls}
            custom={1}
            d="M16 2v4"
          />
          <motion.path
            variants={pathVariants}
            initial="visible"
            animate={pathControls}
            custom={2}
            d="M3 10h18"
          />
          <motion.path
            variants={pathVariants}
            initial="visible"
            animate={pathControls}
            custom={3}
            d="M8 2v4"
          />
          <motion.g
            variants={cogVariants}
            initial="visible"
            animate={cogControls}
          >
            <path d="m15.2 16.9-.9-.4" />
            <path d="m15.2 19.1-.9.4" />
            <path d="m16.9 15.2-.4-.9" />
            <path d="m16.9 20.8-.4.9" />
            <path d="m19.5 14.3-.4.9" />
            <path d="m19.5 21.7-.4-.9" />
            <path d="m21.7 16.5-.9.4" />
            <path d="m21.7 19.5-.9-.4" />
            <circle cx="18" cy="18" r="3" />
          </motion.g>
        </svg>
      </SvgIcon>
    );
  }
);

CalendarCogIcon.displayName = 'CalendarCogIcon';

export { CalendarCogIcon };
